// ============================================================================
// Delhivery MULTISTOP pickup modal — one rider collects from several vendors (multiple pickup POs)
// and drops at a single customer. Ops assembles an ordered PO list; the pickup order is set by
// dragging the rows (native HTML5 drag). Three areas:
//   1. Selected POs — index 0 badged "Pickup", the rest "Stop 1, Stop 2…"; drag by the handle to
//      reorder, Remove to drop. The shared customer drop is pinned at the bottom and can't be moved.
//   2. Route map — every pickup + the drop, drawn along real roads with live per-leg distance/ETA.
//   3. Vehicle picker — the quote's vehicles[], recommended one leading; the pick is sent to book
//      (omit → backend priority-1 default). Same UI as the single-PO booking modal.
//   4. Suggestions — same-customer POs that can be clubbed; each with "Add" (appends + re-quotes).
//   5. Quote panel — the cargo summary (weight, items, max length); a spinner while re-quoting; the
//      quote_error as a warning when the set is clubbable but not quotable right now.
// Re-quotes (debounced ~400ms) on open and after every add / remove / reorder — which also refreshes
// the vehicle list (fares change with the route). Book needs ≥2 POs, a live quote, and an eligible
// vehicle; any quote 4xx surfaces inline and disables Book until the set is valid again.
// Self-contained; talks only to the delhivery-multistop-* helpers in ../../../apis/po.api.
// ============================================================================
import {
  Alert,
  Button,
  Divider,
  Empty,
  Modal,
  Result,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CarOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  HolderOutlined,
  PlusOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import React from "react";
import {
  MultistopContacts,
  MultistopDrop,
  MultistopItem,
  MultistopLeg,
  MultistopQuote,
  MultistopStops,
  MultistopVehicle,
  delhiveryMultistopBook,
  delhiveryMultistopCancel,
  delhiveryMultistopQuote,
  getDelhiveryBookings,
} from "../../../apis/po.api";
import DelhiveryMultistopMap, {
  MultistopMapStop,
} from "./delhivery_multistop_map";
import ContactSelect from "./contact_select";

const { Text, Title } = Typography;

const QUOTE_DEBOUNCE_MS = 400;

const rupee = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `₹${v}`;

// A 10-digit Indian mobile (starts 6–9). Empty is allowed (keeps the backend default).
const PHONE_RE = /^[6-9]\d{9}$/;
const isValidPhone = (v: string | null | undefined) => !!v && PHONE_RE.test(v.trim());
const phoneFieldError = (v: string | null | undefined) =>
  !!v && v.trim() !== "" && !isValidPhone(v);

// The route position label for a selected PO: index 0 is the first pickup, the rest are stops.
const legLabel = (index: number) =>
  index === 0 ? "Pickup" : `Stop ${index}`;

// "1st pickup / 2nd pickup / …" ordinal for the selected-PO ordering hint.
const ordinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// A one-line "city · pincode" locality, skipping the blanks.
const locality = (leg: { city: string | null; pincode: string | null }) =>
  [leg.city, leg.pincode].filter(Boolean).join(" · ") || "—";

// One item across the whole trip, tagged with which pickup it comes from.
interface TripItem extends MultistopItem {
  po: string;
  vendor: string | null;
}

// One selected PO in the route — drag it by the handle to reorder the pickups, and edit its pickup
// POC inline.
const SelectedRow: React.FC<{
  index: number;
  total: number;
  po: string;
  leg?: MultistopLeg;
  contact: string;
  onContactChange: (po: string, value: string) => void;
  onRemove: (po: string) => void;
  disabled?: boolean;
  // Drag-and-drop wiring (owned by the parent, which holds the ordered `route`).
  dragging?: boolean; // this row is the one being dragged
  dropTarget?: boolean; // a dragged row is hovering over this one
  onDragStart: (index: number) => void;
  onDragOverRow: (index: number, e: React.DragEvent) => void;
  onDropRow: (index: number) => void;
  onDragEnd: () => void;
}> = ({
  index,
  total,
  po,
  leg,
  contact,
  onContactChange,
  onRemove,
  disabled,
  dragging,
  dropTarget,
  onDragStart,
  onDragOverRow,
  onDropRow,
  onDragEnd,
}) => {
  const canDrag = !disabled && total >= 2;
  const contactError = phoneFieldError(contact);
  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => {
        if (!canDrag) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = "move";
        // Required or some browsers never fire dragover/drop (Firefox especially).
        e.dataTransfer.setData("text/plain", po);
        onDragStart(index);
      }}
      onDragOver={(e) => onDragOverRow(index, e)}
      onDrop={(e) => {
        e.preventDefault();
        onDropRow(index);
      }}
      onDragEnd={onDragEnd}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 12px",
        marginBottom: 8,
        borderRadius: 10,
        border: `1.5px solid ${
          dropTarget ? "#24AAB7" : index === 0 ? "#24AAB7" : "#f0f0f0"
        }`,
        background: index === 0 ? "#e6fbfc" : "#fff",
        opacity: dragging ? 0.4 : 1,
        // Cue the drop position with a thicker top edge while a row hovers here.
        boxShadow: dropTarget ? "inset 0 3px 0 0 #24AAB7" : "none",
        transition: "opacity 0.12s ease, box-shadow 0.12s ease",
      }}
    >
      <Tooltip title={canDrag ? "Drag to reorder" : undefined}>
        <span
          style={{
            display: "inline-flex",
            color: canDrag ? "#8c8c8c" : "#d9d9d9",
            cursor: canDrag ? "grab" : "not-allowed",
            fontSize: 16,
          }}
        >
          <HolderOutlined />
        </span>
      </Tooltip>
      <div
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: index === 0 ? "#24AAB7" : "#8c8c8c",
          color: "#fff",
          fontSize: 12,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {index + 1}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Space size={8} align="center" wrap>
          <Text strong>{po}</Text>
          <Tag color={index === 0 ? "cyan" : "default"} style={{ marginInlineEnd: 0 }}>
            {legLabel(index)}
          </Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {ordinal(index + 1)} pickup
          </Text>
        </Space>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
          <ShopOutlined style={{ marginRight: 4 }} />
          {leg?.vendor || "—"} · {leg ? locality(leg) : "—"}
        </div>
        {/* Editable pickup POC — a dropdown of the quote's contact options + delivery team, or a
            typed "Other" number. Don't let dragging inside it start a row drag. */}
        <div draggable={false} onDragStart={(e) => e.stopPropagation()}>
          <ContactSelect
            options={leg?.pickup_contact_options}
            value={contact}
            onChange={(v) => onContactChange(po, v)}
            width={200}
          />
        </div>
        {contactError && (
          <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 2 }}>
            Enter a valid 10-digit mobile
          </div>
        )}
      </div>
      <Tooltip title="Remove from this route">
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={disabled}
          onClick={() => onRemove(po)}
        />
      </Tooltip>
    </div>
  );
};

// The single shared customer drop, pinned to the bottom of the route, with the editable drop POC.
const DropRow: React.FC<{
  drop: MultistopDrop | null;
  contact: string;
  onContactChange: (value: string) => void;
}> = ({ drop, contact, onContactChange }) => {
  const contactError = phoneFieldError(contact);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 12px",
        borderRadius: 10,
        border: "1.5px dashed #d9d9d9",
        background: "#fafafa",
      }}
    >
      <EnvironmentOutlined
        style={{ fontSize: 18, color: "#1677ff", width: 56, textAlign: "center", marginTop: 2 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Space size={8} align="center" wrap>
          <Text strong>{drop?.name || "Customer"}</Text>
          <Tag color="blue" style={{ marginInlineEnd: 0 }}>
            Drop
          </Tag>
        </Space>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
          {drop?.address || (drop ? locality(drop) : "—")}
        </div>
        {/* Editable drop POC — same dropdown as the pickups. */}
        <ContactSelect
          options={drop?.drop_contact_options}
          value={contact}
          onChange={onContactChange}
          width={200}
        />
        {contactError && (
          <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 2 }}>
            Enter a valid 10-digit mobile
          </div>
        )}
      </div>
    </div>
  );
};

// Trip cargo: the summary stats (total weight / item count / max length) plus every item across all
// pickups in one combined list, each tagged with its vendor. Spinner overlays it while re-quoting.
const CargoPanel: React.FC<{
  quote: MultistopQuote | null;
  quoteError: string | null;
  loading: boolean;
  ready: boolean; // ≥2 POs selected — otherwise it's suggest-only and there's nothing to show
  items: TripItem[];
}> = ({ quote, quoteError, loading, ready, items }) => {
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 10,
        padding: 16,
        minHeight: 96,
        position: "relative",
        background: "#fff",
      }}
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.6)",
            borderRadius: 10,
            zIndex: 1,
          }}
        >
          <Spin />
        </div>
      )}
      {!ready ? (
        <Text type="secondary">Add at least 2 POs to get a vehicle and fare.</Text>
      ) : quoteError ? (
        <Alert type="warning" showIcon message="Not quotable right now" description={quoteError} />
      ) : (
        <>
          <Space size={24} wrap>
            <Stat
              label="Total weight"
              value={quote?.weight_kg != null ? `${quote.weight_kg} kg` : "—"}
              strong
            />
            <Stat
              label="Items"
              value={quote?.item_count != null ? String(quote.item_count) : String(items.length)}
              strong
            />
            <Stat
              label="Max length"
              value={quote?.max_length_ft != null ? `${quote.max_length_ft} ft` : "—"}
              strong
            />
          </Space>
          {items.length > 0 && (
            <div style={{ marginTop: 14, maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
              {items.map((it) => {
                const excluded = it.included === false;
                const meta = [
                  `${it.quantity}${it.unit ? ` ${it.unit}` : ""}`,
                  it.weight_kg != null ? `${it.weight_kg} kg` : null,
                  it.category || null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <div
                    key={`${it.po}-${it.allocation_item_id}`}
                    style={{
                      display: "flex",
                      gap: 8,
                      padding: "6px 0",
                      borderTop: "1px solid #f5f5f5",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, color: excluded ? "#bbb" : undefined }}>
                        {it.name}
                      </span>
                      {excluded && (
                        <Tag style={{ marginLeft: 6, marginInlineEnd: 0 }}>not shipped</Tag>
                      )}
                      <div style={{ fontSize: 11, color: "#8c8c8c" }}>{meta}</div>
                    </div>
                    {it.vendor && (
                      <Tooltip title={it.po}>
                        <Tag style={{ marginInlineEnd: 0, height: "fit-content" }}>
                          <ShopOutlined style={{ marginRight: 4 }} />
                          {it.vendor}
                        </Tag>
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => (
  <div>
    <div style={{ fontSize: 11, color: "#8c8c8c" }}>{label}</div>
    <div style={{ fontSize: strong ? 18 : 14, fontWeight: strong ? 600 : 400 }}>{value}</div>
  </div>
);

// Local vehicle images (public/images/), keyed by the quote's `type`; unmapped types fall back to an
// icon. Mirrors the single-PO booking modal so the two pickers look identical.
const VEHICLE_IMAGES: Record<string, string> = {
  "2-wheeler": "/images/2_wheeler.webp",
  "mini-3w": "/images/mini_3w.webp",
  "3-wheeler": "/images/3_wheeler.webp",
  "tata-ace": "/images/tata_ace.webp",
  "8ft-pickup": "/images/8_ft.webp",
};

const VehicleThumb: React.FC<{ type: string }> = ({ type }) => {
  const [errored, setErrored] = React.useState(false);
  const src = VEHICLE_IMAGES[type];
  if (src && !errored)
    return (
      <img
        src={src}
        alt={type}
        onError={() => setErrored(true)}
        style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 4 }}
      />
    );
  return (
    <CarOutlined
      style={{ fontSize: 28, color: "#8c8c8c", width: 44, textAlign: "center" }}
      aria-label={type}
    />
  );
};

// Vehicle picker — the selectable list from the quote's vehicles[], sorted by priority. Eligible rows
// toggle the chosen mode; ineligible ones (over capacity) are shown greyed with the reason. Mirrors
// the single-PO booking modal.
const VehiclePicker: React.FC<{
  vehicles: MultistopVehicle[];
  selected: string | undefined; // undefined → use recommended
  onSelect: (type: string | undefined) => void;
}> = ({ vehicles, selected, onSelect }) => (
  <div>
    <style>{`
      .ms-vehicle-row:not([aria-disabled="true"]):hover {
        border-color: #24AAB7 !important;
        box-shadow: 0 2px 10px rgba(36,170,183,0.15);
      }
    `}</style>
    {vehicles.map((v) => {
      const ineligible = v.eligible === false;
      const isSel = v.type === selected;
      return (
        <div
          key={v.type}
          role="button"
          aria-disabled={ineligible}
          aria-pressed={isSel}
          onClick={() => !ineligible && onSelect(isSel ? undefined : v.type)}
          className="ms-vehicle-row"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 14px",
            marginBottom: 10,
            borderRadius: 10,
            border: `1.5px solid ${isSel ? "#24AAB7" : "#f0f0f0"}`,
            background: isSel ? "#e6fbfc" : "#fff",
            cursor: ineligible ? "not-allowed" : "pointer",
            opacity: ineligible ? 0.55 : 1,
            transition: "border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease",
            boxShadow: isSel ? "0 1px 6px rgba(36,170,183,0.18)" : "none",
          }}
        >
          <VehicleThumb type={v.type} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space wrap align="center" size={8}>
              <Text strong style={{ textTransform: "capitalize" }}>
                {v.type}
              </Text>
              {v.priority === 1 && (
                <Tag color="green" style={{ marginInlineEnd: 0 }}>
                  Recommended
                </Tag>
              )}
              {ineligible && (
                <Tooltip title={v.ineligible_reason || undefined}>
                  <Tag color="red" style={{ marginInlineEnd: 0 }}>
                    Unavailable
                  </Tag>
                </Tooltip>
              )}
            </Space>
            {v.capacity?.value != null && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  up to {v.capacity.value} {v.capacity.unit ?? "kg"}
                </Text>
              </div>
            )}
            {v.max_dimensions && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Max size: {v.max_dimensions}
                </Text>
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <Text strong style={{ fontSize: 15 }}>
              {rupee(v.fare?.amount)}
            </Text>
          </div>
        </div>
      );
    })}
  </div>
);

// Pick the default vehicle when the user hasn't chosen one: priority-1, else the first eligible.
const recommendedVehicle = (vehicles: MultistopVehicle[]): MultistopVehicle | undefined =>
  vehicles.find((v) => v.priority === 1) ?? vehicles.find((v) => v.eligible !== false);

export interface DelhiveryMultistopModalProps {
  // Seed PO(s). Index 0 is the first pickup; a single PO opens in suggest-only mode.
  poNumbers: string[];
  open: boolean;
  onClose: () => void;
  // Fired after a successful booking so the caller can refresh its list.
  onBooked?: (poNumbers: string[]) => void;
}

const DelhiveryMultistopModalBody: React.FC<DelhiveryMultistopModalProps> = ({
  poNumbers,
  onClose,
  onBooked,
}) => {
  // The ordered route — source of truth for what we quote and book. Index 0 = first pickup.
  const [route, setRoute] = React.useState<string[]>([]);
  // Vendor/city/pincode per PO, merged from each quote's `selected` (and kept for POs we add).
  const [legs, setLegs] = React.useState<Record<string, MultistopLeg>>({});
  const [drop, setDrop] = React.useState<MultistopDrop | null>(null);
  const [suggestions, setSuggestions] = React.useState<MultistopLeg[]>([]);
  // Route-map inputs from the quote: the Maps key and pickup coords keyed by vendor name.
  const [apiKey, setApiKey] = React.useState<string | null>(null);
  const [stopCoords, setStopCoords] = React.useState<MultistopStops>({});
  const [quote, setQuote] = React.useState<MultistopQuote | null>(null);
  const [quoteError, setQuoteError] = React.useState<string | null>(null);
  // Selectable vehicles from the quote, and the user's pick (undefined → use the recommended one).
  const [vehicles, setVehicles] = React.useState<MultistopVehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = React.useState<string | undefined>();
  // Per-handover OTP flags for this trip — sent (as real booleans) only on book. Both default OFF.
  // "drop" covers the final customer drop + every vendor stop; "pickup" covers the first pickup.
  const [dropOtpEnabled, setDropOtpEnabled] = React.useState(false);
  const [pickupOtpEnabled, setPickupOtpEnabled] = React.useState(false);
  // A 4xx on the quote — the selected set itself can't be clubbed. Disables Book until it clears.
  const [setError, setSetError] = React.useState<string | null>(null);
  const [quoting, setQuoting] = React.useState(false);

  // True when the backend reordered the pickups into an optimized route → show the hint chip.
  const [routeOptimized, setRouteOptimized] = React.useState(false);

  // Editable POCs. `pickupContacts` is PO → 10-digit number; `dropContact` is the customer's. Both
  // prefill from the quote (untouched fields only) and are sent (valid ones) on quote + book.
  const [pickupContacts, setPickupContacts] = React.useState<Record<string, string>>({});
  const [dropContact, setDropContact] = React.useState("");
  // Refs so the (stable) runQuote can read the latest contacts without re-subscribing.
  const pickupContactsRef = React.useRef(pickupContacts);
  pickupContactsRef.current = pickupContacts;
  const dropContactRef = React.useRef(dropContact);
  dropContactRef.current = dropContact;
  // Fields ops has edited — prefill never overwrites these.
  const touchedPickupsRef = React.useRef<Set<string>>(new Set());
  const dropTouchedRef = React.useRef(false);
  // Bumped on a meaningful contact edit (a valid or cleared field) to trigger a re-quote.
  const [contactsVersion, setContactsVersion] = React.useState(0);

  const [booking, setBooking] = React.useState(false);
  const [bookError, setBookError] = React.useState<string | null>(null);
  // The overall flow phase. Booking is async (202) → we submit ("booking"), poll /delhivery-bookings/
  // for the CRN ("booking" continues), then land on "success" or "failed".
  const [phase, setPhase] = React.useState<"form" | "booking" | "success" | "failed">("form");
  const [bookedPos, setBookedPos] = React.useState<string[]>([]); // POs we submitted (for polling)
  const [result, setResult] = React.useState<{
    order_id: string;
    booking_id: number | null;
    tracking_url: string | null;
    vehicle_mode: string | null;
    fare: number | null;
    po_numbers: string[];
  } | null>(null);
  // Post-book cancel flow.
  const [cancelling, setCancelling] = React.useState(false);
  const [cancelStatus, setCancelStatus] = React.useState<string | null>(null);
  const [cancelError, setCancelError] = React.useState<string | null>(null);
  const [pickupStarted, setPickupStarted] = React.useState(false); // 409 → hide Cancel

  // Ignore stale in-flight quotes (only the latest request's response is applied).
  const reqIdRef = React.useRef(0);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keeps the booking poll loop alive; flip to false to stop it (success, or unmount).
  const pollActiveRef = React.useRef(false);
  React.useEffect(() => () => {
    pollActiveRef.current = false;
  }, []);

  // Build the contacts payload — only fields that pass client-side validation (so a half-typed number
  // never 400s the quote; the backend keeps the default for any omitted PO).
  const buildContacts = React.useCallback((): MultistopContacts => {
    const pickup: Record<string, string> = {};
    Object.entries(pickupContactsRef.current).forEach(([po, v]) => {
      if (isValidPhone(v)) pickup[po] = v.trim();
    });
    const out: MultistopContacts = {};
    if (Object.keys(pickup).length > 0) out.pickup_contacts = pickup;
    if (isValidPhone(dropContactRef.current)) out.drop_contact_number = dropContactRef.current.trim();
    return out;
  }, []);

  // A user edit to a pickup/drop contact: remember it's touched, and re-quote once it's valid/cleared.
  const editPickupContact = (po: string, value: string) => {
    touchedPickupsRef.current.add(po);
    setPickupContacts((prev) => ({ ...prev, [po]: value }));
    if (value.trim() === "" || isValidPhone(value)) setContactsVersion((v) => v + 1);
  };
  const editDropContact = (value: string) => {
    dropTouchedRef.current = true;
    setDropContact(value);
    if (value.trim() === "" || isValidPhone(value)) setContactsVersion((v) => v + 1);
  };

  // Seed the route once on open (dedupe, preserve order).
  React.useEffect(() => {
    const seeded = poNumbers.filter((p, i) => p && poNumbers.indexOf(p) === i);
    setRoute(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runQuote = React.useCallback(async (list: string[]) => {
    const id = ++reqIdRef.current;
    setQuoting(true);
    const res = await delhiveryMultistopQuote(list, buildContacts());
    if (id !== reqIdRef.current) return; // a newer quote superseded this one
    setQuoting(false);
    if (res.ok && res.data) {
      setSetError(null);
      setDrop(res.data.drop ?? null);
      setSuggestions(res.data.suggestions ?? []);
      setQuote(res.data.quote ?? null);
      setQuoteError(res.data.quote_error ?? null);
      setRouteOptimized(res.data.route_optimized === true);
      // Prefill POCs for any field ops hasn't touched or filled yet. Default both ends to the
      // assigned ops owner's number; fall back to the leg's / drop's own contact when it's absent.
      const assigned = res.data.assigned_to_contact || "";
      if (Array.isArray(res.data.selected)) {
        setPickupContacts((prev) => {
          const next = { ...prev };
          res.data!.selected.forEach((leg) => {
            const val = assigned || leg.contact_number;
            if (
              val &&
              !touchedPickupsRef.current.has(leg.po_number) &&
              !next[leg.po_number]
            )
              next[leg.po_number] = val;
          });
          return next;
        });
      }
      const dropVal = assigned || res.data.drop?.contact_number;
      if (dropVal && !dropTouchedRef.current && !dropContactRef.current) setDropContact(dropVal);
      // Refresh the vehicle list (fares change with the route). Keep the user's pick only if it's
      // still an eligible option; otherwise fall back to the recommended default (undefined).
      const nextVehicles = res.data.vehicles ?? [];
      setVehicles(nextVehicles);
      setSelectedVehicle((cur) =>
        cur && nextVehicles.some((v) => v.type === cur && v.eligible !== false)
          ? cur
          : undefined
      );
      // Keep the last-known map key/coords if a given response omits them.
      if (res.data.apiKey) setApiKey(res.data.apiKey);
      if (res.data.stops) setStopCoords(res.data.stops);
      // Merge freshest vendor/locality for the selected legs so the rows stay labelled.
      if (Array.isArray(res.data.selected) && res.data.selected.length > 0) {
        setLegs((prev) => {
          const next = { ...prev };
          res.data!.selected.forEach((leg) => (next[leg.po_number] = leg));
          return next;
        });
      }
    } else {
      // 4xx → the selected set can't be clubbed. Keep the route intact so ops can fix it; the quote
      // panel and Book are disabled until the set is valid again.
      setSetError(res.error || "This set of POs can't be clubbed into one pickup.");
      setQuote(null);
      setQuoteError(null);
    }
  }, [buildContacts]);

  // Re-quote (debounced) whenever the route OR a (valid) contact changes — open, add, remove,
  // reorder, contact edit.
  React.useEffect(() => {
    if (phase !== "form") return; // submitting/booked — stop quoting
    if (timerRef.current) clearTimeout(timerRef.current);
    if (route.length === 0) {
      setDrop(null);
      setSuggestions([]);
      setQuote(null);
      setQuoteError(null);
      setSetError(null);
      setVehicles([]);
      setSelectedVehicle(undefined);
      setRouteOptimized(false);
      return;
    }
    setQuoting(true); // show the spinner immediately, even during the debounce window
    timerRef.current = setTimeout(() => runQuote(route), QUOTE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [route, contactsVersion, runQuote, phase]);

  // Drag-and-drop reorder of the pickups. `dragIdx` is the row being dragged, `overIdx` the row it's
  // hovering over. Dropping splices the dragged PO into the target slot; the effect above then
  // re-quotes and the map refreshes. The customer drop isn't in `route`, so it can never be moved.
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);
  const [overIdx, setOverIdx] = React.useState<number | null>(null);

  const onDragStart = (index: number) => setDragIdx(index);

  const onDragOverRow = (index: number, e: React.DragEvent) => {
    if (dragIdx === null) return;
    e.preventDefault(); // allow the drop
    e.dataTransfer.dropEffect = "move";
    if (index !== overIdx) setOverIdx(index);
  };

  const onDropRow = (index: number) => {
    setRoute((prev) => {
      if (dragIdx === null || dragIdx === index) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIdx(null);
    setOverIdx(null);
  };

  const onDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  const remove = (po: string) => setRoute((prev) => prev.filter((p) => p !== po));

  const add = (po: string) =>
    setRoute((prev) => (prev.includes(po) ? prev : [...prev, po]));

  // The ordered points for the route map: each pickup (coords via stopCoords[leg.vendor], in route
  // order) then the customer drop last. Legs without coordinates yet are simply skipped.
  const mapStops = React.useMemo<MultistopMapStop[]>(() => {
    const pts: MultistopMapStop[] = [];
    route.forEach((po, i) => {
      const leg = legs[po];
      const coord = leg?.vendor ? stopCoords[leg.vendor] : undefined;
      if (coord && coord.lat != null && coord.lng != null) {
        pts.push({
          id: po,
          title: `${legLabel(i)} · ${po}`,
          subtitle: leg?.vendor || undefined,
          position: { lat: coord.lat, lng: coord.lng },
        });
      }
    });
    if (drop && drop.lat != null && drop.lng != null) {
      pts.push({
        id: "__drop__",
        title: `Drop · ${drop.name || "Customer"}`,
        subtitle: drop.address || undefined,
        isDrop: true,
        position: { lat: drop.lat, lng: drop.lng },
      });
    }
    return pts;
  }, [route, legs, stopCoords, drop]);

  // Every item across all pickups, in route order, tagged with its vendor/PO — the combined cargo list.
  const tripItems = React.useMemo<TripItem[]>(() => {
    const out: TripItem[] = [];
    route.forEach((po) => {
      const leg = legs[po];
      (leg?.items ?? []).forEach((it) =>
        out.push({ ...it, po, vendor: leg?.vendor ?? null })
      );
    });
    return out;
  }, [route, legs]);

  // Chosen vehicle: the user's pick, else the recommended default. `selectedVehicle` (may be
  // undefined) is what we send to book — omit to let the backend apply its priority-1 default.
  const chosenVehicle =
    vehicles.find((v) => v.type === selectedVehicle) ?? recommendedVehicle(vehicles);

  // Poll /delhivery-bookings/ for the async booking's result. It's all-or-nothing (one consolidated
  // CRN), so we look for any of our POs surfacing an order_id, then show that CRN. Poll ~every 4s for
  // ~60s; if no CRN appears, treat it as a failure. Vehicle/fare come from the chosen quote row, since
  // the 202 doesn't echo them.
  const pollForBooking = async (
    pos: string[],
    vehicleMode: string | null,
    fare: number | null
  ) => {
    pollActiveRef.current = true;
    const ATTEMPTS = 15;
    const INTERVAL_MS = 4000;
    for (let i = 0; i < ATTEMPTS; i++) {
      await new Promise((r) => setTimeout(r, INTERVAL_MS));
      if (!pollActiveRef.current) return; // unmounted / superseded
      const res = await getDelhiveryBookings({ po_number: pos[0], limit: 10 });
      if (!pollActiveRef.current) return;
      // A live row for our PO carrying a CRN = the consolidated booking landed.
      const row = res?.results?.find(
        (r) => pos.includes(r.po_number) && r.order_id && !r.is_deleted
      );
      if (row?.order_id) {
        setResult({
          order_id: row.order_id,
          booking_id: row.booking_id ?? null,
          tracking_url: row.tracking_url ?? null,
          vehicle_mode: vehicleMode,
          fare,
          po_numbers: pos,
        });
        setPhase("success");
        onBooked?.(pos);
        pollActiveRef.current = false;
        return;
      }
    }
    // No CRN after the polling window → all-or-nothing failure.
    pollActiveRef.current = false;
    setPhase("failed");
  };

  // Turning drop OTP ON fans out to every stop, so confirm first. "Okay" enables it; "Review"
  // leaves it off (the switch is controlled, so it doesn't flip until confirmed). Turning OFF is
  // immediate.
  const onToggleDropOtp = (checked: boolean) => {
    if (!checked) {
      setDropOtpEnabled(false);
      return;
    }
    Modal.confirm({
      title: "Enable OTP for every stop?",
      content: "Enabling OTP for drop would enable OTP for each stop by default.",
      okText: "Okay",
      cancelText: "Review",
      onOk: () => setDropOtpEnabled(true),
    });
  };

  const runBook = async () => {
    setBookError(null);
    setBooking(true);
    // Snapshot vehicle/fare now — the 202 won't echo them back.
    const vehicleMode = chosenVehicle?.type ?? null;
    const fare = chosenVehicle?.fare?.amount ?? quote?.fare ?? null;
    const res = await delhiveryMultistopBook(route, {
      vehicle_mode: selectedVehicle,
      pickup_otp_enabled: pickupOtpEnabled,
      drop_otp_enabled: dropOtpEnabled,
      ...buildContacts(),
    });
    setBooking(false);
    if (res.ok) {
      // 202 Accepted — enqueued. Move to the in-progress screen and poll for the CRN.
      const pos = res.data?.accepted?.length ? res.data.accepted : route;
      setBookedPos(pos);
      setPhase("booking");
      message.info("Booking in progress — fetching your Delhivery order…");
      pollForBooking(pos, vehicleMode, fare);
    } else if (res.status === 409) {
      // A PO in the set got booked elsewhere. Keep the form so ops can drop it and retry.
      setBookError(
        res.error || "A PO in this set already has an active booking. Remove it and re-quote."
      );
    } else {
      setBookError(res.error || "Could not book this multistop pickup with Delhivery.");
    }
  };

  // Cancel the whole trip (before the first pickup is collected). A 409 means collection has started
  // → surface the message and hide Cancel for good.
  const runCancel = async () => {
    if (!result) return;
    setCancelError(null);
    setCancelling(true);
    const res = await delhiveryMultistopCancel(
      result.order_id
        ? { order_id: result.order_id }
        : { booking_id: result.booking_id ?? undefined }
    );
    setCancelling(false);
    if (res.ok && res.data) {
      setCancelStatus(res.data.status || "cancelled");
      message.success(
        res.data.status === "cancellation_requested"
          ? "Cancellation requested"
          : "Trip cancelled"
      );
    } else if (res.status === 409) {
      setPickupStarted(true);
      setCancelError(
        res.error || "Pickup has already started — cancel isn't possible; use reattempt delivery."
      );
    } else {
      setCancelError(res.error || "Couldn't cancel this trip.");
    }
  };

  // Booking submitted, waiting for the async CRN. Poll runs in the background.
  if (phase === "booking") {
    return (
      <div style={{ padding: "24px 8px", textAlign: "center" }}>
        <Spin size="large" />
        <Title level={5} style={{ marginTop: 16, marginBottom: 4 }}>
          Booking in progress…
        </Title>
        <Text type="secondary">
          Delhivery is placing your consolidated order. This can take a few seconds.
        </Text>
        <div style={{ marginTop: 16 }}>
          <Space size={[4, 4]} wrap style={{ justifyContent: "center" }}>
            {bookedPos.map((po) => (
              <Tag key={po}>{po}</Tag>
            ))}
          </Space>
        </div>
      </div>
    );
  }

  // Booking window elapsed with no CRN — all-or-nothing failure.
  if (phase === "failed") {
    return (
      <Result
        status="error"
        title="Booking didn't go through"
        subTitle="No Delhivery order was created for these POs. Re-quote and try again."
        extra={[
          <Button key="retry" type="primary" onClick={() => setPhase("form")}>
            Back to booking
          </Button>,
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      />
    );
  }

  // Post-book summary — CRN, tracking, vehicle, fare, PO chips, and whole-trip Cancel (until pickup
  // starts). Read-only.
  if (phase === "success" && result) {
    const cancelled = !!cancelStatus;
    return (
      <div style={{ padding: "8px 4px" }}>
        <Result
          status="success"
          style={{ paddingBottom: 8 }}
          title="Multistop pickup booked"
          subTitle={
            <Space direction="vertical" size={4}>
              <Text>
                Order <Text strong copyable>{result.order_id}</Text>
              </Text>
              <Text type="secondary">
                {result.vehicle_mode || "—"} · {rupee(result.fare)}
              </Text>
            </Space>
          }
        />

        {/* Which POs ride this one order. */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
            {result.po_numbers.length} PO{result.po_numbers.length === 1 ? "" : "s"} on this trip
          </Text>
          <Space size={[4, 4]} wrap>
            {result.po_numbers.map((po) => (
              <Tag key={po}>{po}</Tag>
            ))}
          </Space>
        </div>

        {cancelStatus && (
          <Alert
            style={{ marginBottom: 12 }}
            type={cancelStatus === "cancellation_requested" ? "warning" : "info"}
            showIcon
            message={
              cancelStatus === "cancellation_requested"
                ? "Cancellation requested"
                : "Trip cancelled"
            }
          />
        )}
        {cancelError && (
          <Alert
            style={{ marginBottom: 12 }}
            type="warning"
            showIcon
            message={cancelError}
          />
        )}

        <Space>
          {result.tracking_url && (
            <Button
              type="primary"
              href={result.tracking_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Track on Delhivery ↗
            </Button>
          )}
          {/* Cancel is whole-trip and only before the first pickup — hidden once cancelled or a 409
              says pickup has started. */}
          {!cancelled && !pickupStarted && (
            <Button danger loading={cancelling} onClick={runCancel}>
              Cancel trip
            </Button>
          )}
          <Button onClick={onClose}>Done</Button>
        </Space>
      </div>
    );
  }

  const ready = route.length >= 2;
  // A live quote implies an eligible vehicle exists; also guard that the chosen one is eligible.
  const vehicleOk = chosenVehicle != null && chosenVehicle.eligible !== false;
  // No contact field may hold a non-empty invalid number (empty is fine — keeps the default).
  const contactsValid =
    !phoneFieldError(dropContact) &&
    Object.values(pickupContacts).every((v) => !phoneFieldError(v));
  const canBook =
    ready &&
    !quoting &&
    !setError &&
    !quoteError &&
    quote != null &&
    vehicleOk &&
    contactsValid &&
    !booking;
  // Vehicles sorted so the recommended one leads (priority 1 first, then remaining eligibles, then
  // ineligibles at the bottom).
  const sortedVehicles = [...vehicles].sort(
    (a, b) => (a.priority ?? 99) - (b.priority ?? 99)
  );
  // Suggestions not already in the route.
  const freshSuggestions = suggestions.filter((s) => !route.includes(s.po_number));

  return (
    <div>
      {/* Selected POs — the ordered route, with the shared drop pinned at the bottom. */}
      <Space align="center" size={8} style={{ marginBottom: 8 }} wrap>
        <Title level={5} style={{ margin: 0 }}>
          Route ({route.length} pickup{route.length === 1 ? "" : "s"})
        </Title>
        {routeOptimized && (
          <Tooltip title="Delhivery reordered the pickups into the shortest route.">
            <Tag color="green" style={{ marginInlineEnd: 0 }}>
              Route auto-optimized
            </Tag>
          </Tooltip>
        )}
      </Space>
      {route.length === 0 ? (
        <Empty description="No POs selected" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div>
          {route.length >= 2 && (
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
              Drag the pickups to set the route order — the customer drop stays last.
            </Text>
          )}
          {route.map((po, i) => (
            <SelectedRow
              key={po}
              index={i}
              total={route.length}
              po={po}
              leg={legs[po]}
              contact={pickupContacts[po] ?? ""}
              onContactChange={editPickupContact}
              onRemove={remove}
              disabled={booking}
              dragging={dragIdx === i}
              dropTarget={overIdx === i && dragIdx !== null && dragIdx !== i}
              onDragStart={onDragStart}
              onDragOverRow={onDragOverRow}
              onDropRow={onDropRow}
              onDragEnd={onDragEnd}
            />
          ))}
          <DropRow drop={drop} contact={dropContact} onContactChange={editDropContact} />
        </div>
      )}

      {/* The selected set can't be clubbed (quote 4xx). */}
      {setError && (
        <Alert
          style={{ marginTop: 12 }}
          type="error"
          showIcon
          message="This set can't be clubbed"
          description={setError}
        />
      )}

      {/* Route map — all pickup points + the customer drop, with live distances/ETAs. */}
      {apiKey && mapStops.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Title level={5} style={{ marginBottom: 8 }}>
            Route map
          </Title>
          <DelhiveryMultistopMap apiKey={apiKey} stops={mapStops} />
        </>
      )}

      {/* Trip cargo — the summary stats + every item across all pickups, below the map. */}
      {ready && !setError && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Title level={5} style={{ marginBottom: 8 }}>
            Items ({tripItems.length})
          </Title>
          <CargoPanel
            quote={quote}
            quoteError={quoteError}
            loading={quoting}
            ready={ready && !setError}
            items={tripItems}
          />
        </>
      )}

      {/* Vehicle picker — choose the truck, like the single-PO booking. */}
      {ready && !setError && sortedVehicles.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Title level={5} style={{ marginBottom: 8 }}>
            Vehicle
          </Title>
          <VehiclePicker
            vehicles={sortedVehicles}
            selected={selectedVehicle}
            onSelect={setSelectedVehicle}
          />
          {selectedVehicle && (
            <Button type="link" style={{ padding: 0 }} onClick={() => setSelectedVehicle(undefined)}>
              Use recommended
            </Button>
          )}
        </>
      )}

      {/* Suggestions — same-customer POs that can be clubbed in. */}
      {freshSuggestions.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Title level={5} style={{ marginBottom: 8 }}>
            Suggested to club ({freshSuggestions.length})
          </Title>
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            {freshSuggestions.map((s) => (
              <div
                key={s.po_number}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 12px",
                  borderRadius: 10,
                  border: "1px solid #f0f0f0",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong>{s.po_number}</Text>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                    <ShopOutlined style={{ marginRight: 4 }} />
                    {s.vendor || "—"} · {locality(s)}
                  </div>
                </div>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  disabled={booking}
                  onClick={() => add(s.po_number)}
                >
                  Add
                </Button>
              </div>
            ))}
          </Space>
        </>
      )}

      {/* Book failure — keep the form intact so ops can retry. */}
      {bookError && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message="Booking failed"
          description={bookError}
          closable
          onClose={() => setBookError(null)}
        />
      )}

      <Divider style={{ margin: "16px 0" }} />

      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space align="start" size={10}>
            <Switch checked={dropOtpEnabled} onChange={onToggleDropOtp} />
            <div>
              <Text strong>OTP at delivery</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Delhivery sends a code to the customer (final drop) and every vendor stop; the
                  rider must collect it to complete each delivery.
                </Text>
              </div>
            </div>
          </Space>
          <Space align="start" size={10}>
            <Switch checked={pickupOtpEnabled} onChange={setPickupOtpEnabled} />
            <div>
              <Text strong>OTP at pickup</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Delhivery sends a code to the first pickup contact(s); the rider must collect it
                  to complete pickup.
                </Text>
              </div>
            </div>
          </Space>
        </Space>
      </div>

      <Space>
        <Tooltip title={!ready ? "Select at least 2 POs to book" : undefined}>
          <Button type="primary" disabled={!canBook} loading={booking} onClick={runBook}>
            Book multistop pickup
            {chosenVehicle
              ? ` · ${chosenVehicle.type} · ${rupee(chosenVehicle.fare?.amount)}${
                  !selectedVehicle ? " (Recommended)" : ""
                }`
              : quote?.fare != null
              ? ` · ${rupee(quote.fare)}`
              : ""}
          </Button>
        </Tooltip>
        <Button onClick={onClose} disabled={booking}>
          Cancel
        </Button>
      </Space>
    </div>
  );
};

// Modal shell. `destroyOnClose` remounts the body on every open, so it re-seeds the route from
// `poNumbers` and clears any prior quote/booking state.
export const DelhiveryMultistopModal: React.FC<DelhiveryMultistopModalProps> = (props) => (
  <Modal
    title="Book multistop pickup"
    open={props.open}
    onCancel={props.onClose}
    footer={null}
    width={860}
    destroyOnClose
  >
    {props.open && <DelhiveryMultistopModalBody {...props} />}
  </Modal>
);

export default DelhiveryMultistopModal;
